import { useQuery } from '@tanstack/react-query';
import { Decimal } from '@mlm/shared';
import { getOrderStockUsage, getTenantFeatures } from '../../lib/api';
import { costLabel } from './lot-bits';

/**
 * "Which stock did this order use?" for the order drawer: every stock-out the
 * order caused (its lines, the parts of a kit or bundle, the materials its
 * recipes used) with the lots each one came out of and what they cost.
 *
 * Renders nothing unless the tenant uses stock lots or recipes, and nothing
 * for an order that has not touched stock yet.
 */
export function OrderStockUsageSection({ orderId }: { orderId: string }) {
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const lotsEnabled = featuresQ.data?.inventory_lots_enabled === true;
  const materialsEnabled = featuresQ.data?.materials_enabled === true;
  const usageQ = useQuery({
    queryKey: ['admin-order-stock-usage', orderId],
    queryFn: () => getOrderStockUsage(orderId),
    enabled: lotsEnabled || materialsEnabled,
  });

  // With lots off the only rows worth showing are the recipe materials; the
  // sold lines themselves are already listed under Items.
  const rows = (usageQ.data ?? []).filter(
    (r) => r.delta !== 0 && (lotsEnabled ? true : r.isMaterial),
  );
  if (rows.length === 0) return null;

  return (
    <section>
      <h3 className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Stock used</h3>
      <ul className="space-y-2">
        {rows.map((r) => {
          const unit = r.unitLabel?.trim() || 'pcs';
          return (
            <li key={r.movementId} className="rounded-md border px-3 py-2 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <p className="min-w-0 truncate font-medium">
                  {r.productName}
                  {r.variantName ? ` (${r.variantName})` : ''}
                </p>
                <p className="shrink-0 tabular-nums">
                  {Math.abs(r.delta).toLocaleString()} {unit}
                  {r.cost != null && (
                    <span className="text-muted-foreground">
                      {' '}
                      · cost {new Decimal(r.cost).toFixed(2)}
                    </span>
                  )}
                </p>
              </div>
              {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
              {r.lots.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {r.lots.map((l, i) => (
                    <li key={`${l.lotId ?? 'pool'}-${i}`} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate">
                        {l.lotId ? (
                          <>
                            Lot <span className="font-mono">{l.lotNo ?? 'unnamed'}</span>
                            {l.expiresOn ? ` · expires ${l.expiresOn}` : ''}
                          </>
                        ) : (
                          'Stock with no lot'
                        )}
                        {l.restoredQty > 0 && ` · ${l.restoredQty} put back`}
                      </span>
                      <span className="shrink-0 tabular-nums">
                        {l.qty.toLocaleString()} × {costLabel(l.unitCost)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
